// Quality area shell (office, Module 07 §4 — slices 07-A + 07-B).
// Sub-navigation over three office screens: the inspection queue (§4.3), the
// Quarantine Board (§4.6, 07-B-3) and the control-point library (§4.1), whose
// editor (§4.2) is a child route of the library.
//
// WHAT IS DELIBERATELY ABSENT (D-033, D-123 §0.2): the guided cut (§4.5) is
// 07-C's, the CoA manager (§4.7) and quality analytics (§4.8) 07-E's. None of
// them gets a link, a route, or a disabled shell — a 404 on a route that does
// not exist is honest, and a tab that leads to a screen with no API behind it
// is not.
//
// NAV GATING IS ONE TABLE (hooks/permissions.ts `qualityAreaEntries`), read by
// the app nav in routes/root.tsx AND by this strip. Module 05 shipped this area
// with two gating rules and five roles that own its codes ended up unable to
// reach it; that write-up is in the waste area's permissions file, and this is
// the same fix applied before rather than after.
//
// RENDER-ONCE (design-system §5). Both entries are permission-decided, so the
// strip's WIDTH — and at 412 px its HEIGHT, since RouterTabs wraps — is decided
// by a role feed that arrives after first paint. Painting before the mirror
// settles drops the page contents by a wrapped row when the last entry
// resolves. While the mirror is outstanding the area is a reserving skeleton
// whose box IS `.ds-tabs`'s pinned min-height; when it settles the whole
// subtree REMOUNTS (`key` swap) so nothing painted early ever moves.

import { useEffect } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton, ForbiddenState } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { qualityAreaEntries, qualityLandingPath, useQualityPermissions } from "./hooks/permissions";
import "./quality.css";

export function QualityLayout() {
  const { t } = useTranslation();
  const perms = useQualityPermissions();

  const tabs: RouterTabDef[] = qualityAreaEntries(perms).map((entry) => ({
    to: entry.to,
    label: t(entry.labelKey),
  }));

  if (!perms.isResolved) {
    return (
      <div className="quality-area" key="quality-loading" data-testid="quality-loading">
        <div className="ds-tabs quality-subnav-skel" aria-hidden="true" />
        <div className="quality-body">
          <CardSkeleton lines={6} label={t("quality.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="quality-area" key="quality-ready">
      <RouterTabs tabs={tabs} label={t("quality.nav.aria")} data-testid="quality-subnav" />
      <div className="quality-body">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * `/quality` — land this principal on the first screen they can actually open.
 *
 * A component rather than a `beforeLoad` redirect for the reason module 05
 * recorded the hard way: `beforeLoad` runs outside React and cannot read the
 * role feed, so it would send every caller to one screen — including `finance`
 * and `sales`, who hold `quality.inspection.read` and NOT `quality.catalog.read`
 * and would land on the one screen in the area they are refused. The parent
 * shell has already reserved the layout, so this renders nothing while it
 * decides.
 */
export function QualityIndexRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useQualityPermissions();
  const to = qualityLandingPath(perms);

  useEffect(() => {
    if (to !== undefined) void navigate({ to, replace: true });
  }, [to, navigate]);

  if (to !== undefined) return null;
  return <ForbiddenState title={t("quality.access.title")} body={t("quality.access.area")} />;
}
